import {QuestionReferences, BranchNode, validator} from "@reside/forms";
import {
  EventTriggerForm,
  TemplateField,
} from "../../services/HubAndSpokeService";
import unflatten from "obj-unflatten";
import {PathVariablesEnum} from "./consts";
import {format, getUnixTime} from "date-fns";
import {DataNode} from "rc-tree/lib/interface";
import {createFacilitiesTree} from "../../models/admin-session/AdminSessionModel.helpers";
import {isFacilityInactive} from "../../services/FacilityService";
import {SimpleFacilityModel} from "../../utils/types/facility";

// Validation rules for the document path
const VALIDATORS = {
  document_path: "required|document-path",
};

// Function to truncate long answer texts
export const truncateAnswer = (answerText: string) =>
  answerText.length > 120 ? `${answerText.substring(0, 120)}...` : answerText;

// Function to create a QuestionReference from a TemplateField
export function CreateQuestionReferenceFromTemplateField(
  templateField: TemplateField,
): QuestionReferences<BranchNode> {
  return {
    id: templateField.question_id,
    type: templateField.question_type as any,
    translationKey: templateField.question_translation_text,
    choices: [
      {
        id: templateField.answer_id,
        translationKey: templateField.answer_translation_text,
      },
    ],
  };
}

// Function to create a form control with a given reference
export function CreateFormControlWithReference(
  reference: QuestionReferences<BranchNode>,
) {
  return {
    id: `form:control-${reference.id}`,
    type: "form:control",
    reference,
    name: `form:control-${reference.id}`,
    state: {
      visible: true,
    },
  };
}

// Validation function for the EventTriggerForm
export const validate = (formValues: EventTriggerForm) => {
  const errors = unflatten(validator.validateAll(formValues, VALIDATORS));

  if (!formValues.upload_on || formValues.upload_on.length === 0) {
    errors.upload_on = "At least one option must be selected";
  }

  return errors;
};

// Function to generate a preview of the path with replaced variables
export const generatePathPreview = (targetPath: string): string => {
  const replaceVariables = (variable: string): string => {
    switch (variable) {
      case PathVariablesEnum.FacilityName:
        return "Example_Care";
      case PathVariablesEnum.OrganizationName:
        return "Example_Health_Inc";
      case PathVariablesEnum.ResidentName:
        return "Mickey_Jay_Johnstone";
      case PathVariablesEnum.ResidentLastName:
        return "Johnstone";
      case PathVariablesEnum.AdmissionDate:
        return format(new Date(), "yyyyMMdd");
      case PathVariablesEnum.CurrentDate:
        return format(new Date(), "yyyyMMdd");
      case PathVariablesEnum.CurrentDateTime:
        return format(new Date(), "yyyyMMddHHmmSS");
      case PathVariablesEnum.CurrentUnixTime:
        return getUnixTime(new Date()).toString();
      case PathVariablesEnum.UUID:
        return "3xAmPle-ex4m-pL3-xamp-ex4mP1e";
      default:
        return "";
    }
  };

  return targetPath.replace(/{[^}]+}/g, replaceVariables);
};

/**
 * Facilities offered in the event trigger facility picker: the active ones, plus the inactive ones already assigned to
 * the trigger. Those stay selectable so that editing the trigger doesn't silently unassign them.
 */
export const getEventTriggerFacilitiesTree = (
  facilities: ReadonlyArray<SimpleFacilityModel>,
  assignedFacilityIds: ReadonlyArray<string>,
): DataNode[] =>
  createFacilitiesTree(
    facilities.filter(
      facility =>
        !isFacilityInactive(facility) ||
        assignedFacilityIds.includes(facility.id),
    ),
  ).map(organization => ({
    ...organization,
    children: organization.children.map(facility => ({
      ...facility,
      title: isFacilityInactive(facility)
        ? `${facility.title} (Inactive)`
        : facility.title,
    })),
  }));
